import { useStore } from '@nanostores/react'
import React, { useEffect, useRef, useState } from 'react'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/scss'
import 'swiper/scss/navigation'

import { Icon } from '@/components/Icon'
import { Link } from '@/components/Link/Link'
import { QuantitySelector } from '@/components/Shared/QuanitySelector'
import type { Addon } from '@/models/addon'
import { AddonGroup } from '@/models/addon-group'
import { loadAddons } from '@/stores/addon/operations'
import { $addons } from '@/stores/addon/stores'
import type {
  CMSAddonsOptionGroup,
  CMSProgramAddonOption,
} from '@/types/sanity'
import { formatPrice } from '@/utilities/number'
import { ExpandableCard } from '@components/Shared/ExpandableCard'

import './Addons.scss'

type Props = {
  content: CMSAddonsOptionGroup
  onSelectedAddonsChange: (args: {
    group: CMSAddonsOptionGroup
    addonsWithQuantity: { addon: CMSProgramAddonOption; quantity: number }[]
  }) => void
}

export const Addons: React.FC<Props> = ({
  content,
  onSelectedAddonsChange,
}) => {
  const [selectedAddons, setSelectedAddons] = useState<{
    [sku: string]: number
  }>({})
  const [isCardExpanded, setIsCardExpanded] = useState(content.isOpen)

  const popsAddons = useStore($addons)
  const group = new AddonGroup(content, popsAddons)
  const max = group.config.optionGroupMaxCount

  const navPrevRef = useRef<HTMLButtonElement>(null)
  const navNextRef = useRef<HTMLButtonElement>(null)

  const getAddonQuantity = (addon: Addon) =>
    selectedAddons[addon.shopifySku] || 0
  const totalPrice = group.addons.reduce((acc, addon) => {
    return acc + getAddonQuantity(addon) * addon.config.variant.price
  }, 0)
  const totalPriceLabel = `Add-ons total: $${totalPrice}`
  const totalCount = group.addons.reduce((acc, addon) => {
    return acc + getAddonQuantity(addon)
  }, 0)
  const totalCountLabel = max
    ? `${totalCount}/${max} added`
    : `${totalCount} added`

  useEffect(() => {
    loadAddons()
  }, [content])

  useEffect(() => {
    if (!group) return
    const addonsWithQuantity = group.addons.reduce(
      (acc, addon) => {
        const quantity = selectedAddons[addon.shopifySku]
        if (!quantity) return acc
        acc.push({ addon: addon.config, quantity: quantity })
        return acc
      },
      [] as { addon: CMSProgramAddonOption; quantity: number }[],
    )
    onSelectedAddonsChange({ group: group.config, addonsWithQuantity })
  }, [selectedAddons])

  const handleAddonChange = ({
    addon,
    quantity,
  }: {
    addon: Addon
    quantity: number
  }) => {
    setSelectedAddons((prev) => ({
      ...prev,
      [addon.shopifySku]: quantity,
    }))
  }
  if (!group) return null

  const handleCardChange = ({ isExpanded }: { isExpanded: boolean }) => {
    setIsCardExpanded(isExpanded)
  }

  const renderAddonsSummary = () => {
    return (
      <div className="addons__summary">
        <div className="addons__quanity-summary type-eyebrow">
          {totalCountLabel}
        </div>
        <div className="addons__price-summary type-eyebrow">
          {totalPriceLabel}
        </div>
      </div>
    )
  }

  return (
    <div className="addons">
      <ExpandableCard
        title={<span className="type-eyebrow">{group.config.label}</span>}
        subtitle={
          !isCardExpanded && totalCount
            ? renderAddonsSummary()
            : group.config.description
        }
        isExpanded={content.isOpen}
        onChange={handleCardChange}
      >
        <div className="addons__card-content">
          <div className="addons__carousel">
            <div className="addons__carousel-navigation">
              <button
                className="addons__carousel-navigation-prev"
                ref={navPrevRef}
              >
                <Icon
                  name="primary-arrow-right"
                  width={20}
                  height={9}
                  viewBox="0 0 21 11"
                />
              </button>
              <button
                className="addons__carousel-navigation-next"
                ref={navNextRef}
              >
                <Icon
                  name="primary-arrow-right"
                  width={20}
                  height={9}
                  viewBox="0 0 21 11"
                />
              </button>
            </div>
            <Swiper
              className="swiper-container"
              spaceBetween={8}
              slidesPerView={'auto'}
              navigation={{
                nextEl: navNextRef?.current,
                prevEl: navPrevRef?.current,
              }}
              modules={[Navigation]}
              scrollbar={{ draggable: true }}
              touchEventsTarget="container"
            >
              {group.addons.map((addon) => (
                <SwiperSlide key={addon.id} className="swiper-slide">
                  <div className="addon-slide">
                    <div className="addon-slide__image-wrapper">
                      {addon.config.imageUrl && (
                        <img
                          src={addon.config.imageUrl}
                          alt={addon.config.label}
                          className="addon-slide__image"
                        />
                      )}
                      <div className="addon-slide__quantity-selector-wrapper">
                        <QuantitySelector
                          value={getAddonQuantity(addon)}
                          min={0}
                          max={
                            max && max - totalCount + getAddonQuantity(addon)
                          }
                          onChange={(q) =>
                            handleAddonChange({ addon, quantity: q })
                          }
                          isLoading={false}
                        />
                      </div>
                    </div>
                    <div className="addon-slide__body">
                      <div className="addon-slide__title type-label">
                        {addon.config.label}
                      </div>
                      <div className="addon-slide__price type-label">
                        {formatPrice(addon.config.variant.price)}
                      </div>
                      {addon.config.additionalInformation && (
                        <Link
                          link={{
                            url: '#',
                            isExternal: false,
                            isModal: true,
                            isAnchor: false,
                            modalContent: addon.config.additionalInformation,
                          }}
                          classes={[
                            'addon-slide__details-link',
                            'type-label',
                            'type-link',
                          ]}
                          modalCloseButtonType="close"
                        >
                          Details
                        </Link>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <div className="addons__summary-wrapper">{renderAddonsSummary()}</div>
        </div>
      </ExpandableCard>
    </div>
  )
}
